import { useEffect, useState } from "react";
import { SpinnerCircular } from "spinners-react";
import SchematicPlanPage from "../../pages/LevelTwo/SchematicPlanPage";
import { branchFeatureFlags } from "../../config/branchFeatureFlags";
import { ErrorToast } from "../toastMessage";
import {
  TSchematicPlanType,
  useSaveSchematicPlanChoice,
  useSchematicPlan,
} from "../../hooks/schematic-plan.hooks";
import { SchematicFileUpload } from "./SchematicFileUpload";
import { OfflineSchematicChoicePanel } from "./OfflineSchematicChoicePanel";
import { SchematicImageDimensionsPanel } from "./SchematicImageDimensionsPanel";
import { clearSavedSchematicDrawing } from "../../utils/schematicDrawingStore";

type Props = {
  /** The application as the payment review screens load it (`id` is the database id). */
  application: any;
  level: 1 | 2 | 3;
  /**
   * Whether this application gets the choice at all. Every payment review
   * screen passes true today, Offline Tentative included; false keeps the
   * drawing only.
   * BEFORE: "Offline Tentative does not (its certificate is already
   * generated); it keeps the drawing only."
   */
  offersChoice: boolean;
  onBack: () => void;
  onForward: () => void;
};

type TChoice = { value: TSchematicPlanType; title: string; hint: string };

const CHOICES: TChoice[] = [
  { value: "drawing", title: "Draw schematic", hint: "Draw the plot on the canvas, as before" },
  { value: "file", title: "Upload file", hint: "One PDF, JPG or PNG, up to 10 MB" },
];

/** The two extra choices an Offline Tentative application gets. */
const OFFLINE_CHOICES: TChoice[] = [
  {
    value: "applicant_file",
    title: "Use applicant's schematic",
    hint: "The Schematic Plan the applicant uploaded with the application",
  },
  { value: "none", title: "No schematic plan", hint: "The certificate is issued without a schematic plan" },
];
const NO_APPLICANT_FILE_HINT = "The applicant did not upload a Schematic Plan";

/** Offered to every application, Offline Tentative included, when the server says so. */
const IMAGE_DIMENSIONS_CHOICE: TChoice = {
  value: "image_dimensions",
  title: "Upload image + dimensions",
  hint: "One JPG or PNG of the plot, the four sides and the direction",
};

/**
 * The Schematic Plan step of the L1/L2/L3 payment review.
 *
 * Level 1 and Level 2 choose between the drawing (SchematicPlanPage, as
 * before) and an uploaded file, and can switch either way; the drawing and
 * the file both stay saved, so switching back brings the other one back.
 * The choice is saved when they press Next. Level 3 cannot switch: it gets
 * whichever one Level 2 forwarded and can edit it (redraw, or replace the
 * file). When nobody chose before Level 3 (Level 3 overrode the application
 * at Level 1's payment review), Level 3 chooses the same way.
 *
 * An Offline Tentative application also gets "Use applicant's schematic"
 * and "No schematic plan" (OfflineSchematicChoicePanel), behind
 * ENABLE_OFFLINE_SCHEMATIC_OPTIONS; until a choice is saved it opens on
 * "No schematic plan" (the server sends that as the plan type).
 *
 * Every application can also get "Upload image + dimensions"
 * (SchematicImageDimensionsPanel), behind ENABLE_SCHEMATIC_IMAGE_DIMENSIONS.
 *
 * With the feature off, or for an application without the choice, this is
 * SchematicPlanPage on its own, exactly as before.
 */
export function SchematicPlanStep({ application, level, offersChoice, onBack, onForward }: Props) {
  const isFeatureOn = branchFeatureFlags.ENABLE_SCHEMATIC_FILE_UPLOAD && offersChoice;
  const applicationId = Number(application?.id);
  const planQuery = useSchematicPlan(isFeatureOn ? applicationId : undefined);
  const saveChoice = useSaveSchematicPlanChoice(applicationId);
  /**
   * BEFORE - Level 3 never got the choice:
   *
   * const canChoose = level !== 3;
   */
  const levelThreeChooses = level === 3 && !!planQuery.data?.levelThreeChooses;
  const canChoose = level !== 3 || levelThreeChooses;

  const file = planQuery.data?.file ?? null;
  const [mode, setMode] = useState<TSchematicPlanType | null>(null);

  /**
   * Offline Tentative also gets "Use applicant's schematic" (greyed out when
   * the applicant uploaded none) and "No schematic plan". The server says
   * whether this application gets them (offlineOptions).
   */
  const offlineOptions =
    branchFeatureFlags.ENABLE_OFFLINE_SCHEMATIC_OPTIONS && !!planQuery.data?.offlineOptions;
  const applicantFile = planQuery.data?.applicantFile ?? null;
  const imageDimensionsOption =
    branchFeatureFlags.ENABLE_SCHEMATIC_IMAGE_DIMENSIONS && !!planQuery.data?.imageDimensionsOption;
  /**
   * With only the new choice on, Offline Tentative gets no choice ("No
   * schematic plan") and every other application gets "Upload image +
   * dimensions" only.
   */
  const imageOnly = branchFeatureFlags.ENABLE_SCHEMATIC_IMAGE_ONLY && imageDimensionsOption;
  const onlyPlanType: TSchematicPlanType | null = imageOnly
    ? offlineOptions ? "none" : "image_dimensions"
    : null;
  /**
   * BEFORE: const choices = offlineOptions ? [...CHOICES, ...OFFLINE_CHOICES] : CHOICES;
   */
  const choices = imageOnly
    ? []
    : [
        ...CHOICES,
        ...(imageDimensionsOption ? [IMAGE_DIMENSIONS_CHOICE] : []),
        ...(offlineOptions ? OFFLINE_CHOICES : []),
      ];

  /**
   * The step opens on the saved choice. With only the new choice on, a level
   * that chooses opens on that one choice whatever was saved before; Level 3
   * without the choice keeps what was forwarded to it.
   * BEFORE: if (planQuery.data && mode === null) setMode(planQuery.data.planType);
   */
  useEffect(() => {
    if (planQuery.data && mode === null) {
      setMode(onlyPlanType && canChoose ? onlyPlanType : planQuery.data.planType);
    }
  }, [planQuery.data, mode, onlyPlanType, canChoose]);

  if (!isFeatureOn) {
    return <SchematicPlanPage application={application} onBack={onBack} onForward={onForward} />;
  }

  if (planQuery.isLoading || mode === null) {
    if (planQuery.isError) {
      return <SchematicPlanPage application={application} onBack={onBack} onForward={onForward} />;
    }
    return (
      <div className="flex justify-center p-10">
        <SpinnerCircular />
      </div>
    );
  }

  /**
   * Saves the choice on every Next, even when it did not change, so the
   * backend knows which level chose; then moves on.
   *
   * BEFORE - saved only when the choice changed:
   *
   * if (!canChoose || savedType === planType) {
   */
  const forwardWith = (planType: TSchematicPlanType) => {
    if (!canChoose) {
      onForward();
      return;
    }
    saveChoice.mutate(planType, {
      onSuccess: (result) => {
        if (result?.success) onForward();
      },
    });
  };

  /** BEFORE: const choiceBar = canChoose ? ( */
  const choiceBar = canChoose && choices.length > 0 ? (
    <div className="px-6 pt-6">
      <p id="schematic-plan-choice" className="text-[#423F3F] font-semibold mb-2">
        How do you want to add the schematic plan?
      </p>
      {levelThreeChooses && (
        <p className="text-sm text-gray-600 mb-3">
          No schematic choice was confirmed before Level 3, so you can choose here.
        </p>
      )}
      <div role="radiogroup" aria-labelledby="schematic-plan-choice" className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
        {/* BEFORE: {CHOICES.map((choice) => { */}
        {choices.map((choice) => {
          const selected = mode === choice.value;
          const unavailable = choice.value === "applicant_file" && !applicantFile;
          return (
            <button
              key={choice.value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={unavailable}
              onClick={() => setMode(choice.value)}
              className={`text-left border-2 rounded-lg px-4 py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                selected ? "border-primary bg-blue-50" : "border-gray-200 bg-white hover:bg-gray-50"
              }`}
            >
              <span className="flex items-center gap-2 font-semibold text-[#423F3F]">
                <span
                  className={`inline-block w-4 h-4 rounded-full border-2 ${
                    selected ? "border-primary bg-primary" : "border-gray-400"
                  }`}
                />
                {choice.title}
              </span>
              <span className="block text-sm text-gray-500 mt-1 ml-6">
                {/* BEFORE: {choice.hint} */}
                {unavailable ? NO_APPLICANT_FILE_HINT : choice.hint}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  ) : null;

  if (mode === "applicant_file" || mode === "none") {
    const chosen = mode;
    return (
      <>
        {choiceBar}
        <OfflineSchematicChoicePanel
          mode={chosen}
          applicantFile={applicantFile}
          canChoose={canChoose}
          isSaving={saveChoice.isPending}
          onBack={onBack}
          onNext={() => {
            /** Neither choice uses a drawing, so any drawing this tab saved is dropped. */
            clearSavedSchematicDrawing(application?.applicationId);
            forwardWith(chosen);
          }}
        />
      </>
    );
  }

  if (mode === "image_dimensions") {
    return (
      <>
        {choiceBar}
        <SchematicImageDimensionsPanel
          applicationId={applicationId}
          level={level}
          applicationNumber={application?.applicationId}
          saved={planQuery.data?.imageDimensions}
          canChoose={canChoose}
          isSaving={saveChoice.isPending}
          onBack={onBack}
          onNext={() => {
            /** The certificate shows the image and sides, so any drawing this tab saved is dropped. */
            clearSavedSchematicDrawing(application?.applicationId);
            forwardWith("image_dimensions");
          }}
        />
      </>
    );
  }

  if (mode === "drawing") {
    return (
      <>
        {choiceBar}
        <SchematicPlanPage
          application={application}
          onBack={onBack}
          onForward={() => forwardWith("drawing")}
        />
      </>
    );
  }

  return (
    <>
      {choiceBar}
      <div className="p-6 max-w-4xl mx-auto">
        <h2 className="text-[#423F3F] text-xl font-bold mb-1">Schematic Plan</h2>
        {!canChoose && (
          <p className="text-sm text-gray-600 mb-4">
            An uploaded file was forwarded to you. You can replace it before Save And Preview.
          </p>
        )}
        <SchematicFileUpload applicationId={applicationId} file={file} />

        <div className="flex flex-col sm:flex-row justify-center gap-3 mt-6">
          <button
            type="button"
            className="bg-black text-white px-10 py-2 grow md:grow-0 rounded-full font-bold"
            onClick={onBack}
          >
            Back
          </button>
          <button
            type="button"
            className="bg-primary px-10 py-2 grow md:grow-0 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            disabled={saveChoice.isPending}
            onClick={() => {
              if (!file) {
                ErrorToast("Upload the schematic plan file first");
                return;
              }
              /**
               * The certificate uses the uploaded file, so any drawing this tab
               * saved while Draw schematic was open is dropped. Left behind, it
               * was sent with the certificate data and the request was refused
               * as too large (see utils/schematicDrawingStore.ts).
               * BEFORE: forwardWith("file") only.
               */
              clearSavedSchematicDrawing(application?.applicationId);
              forwardWith("file");
            }}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}
